/**
 * useEncouragement Hook
 * Manages encouragement messages and feedback
 */

import { useState, useEffect, useCallback } from 'react';
import { EncouragementMessage, FeedbackRating, EncouragementPattern } from '../models/EncouragementMessage';
import { CompletionEntry } from '../models/CompletionEntry';
import { detectPattern } from '../services/encouragement/PatternDetector';
import {
  createEncouragementMessage,
  shouldShowPattern,
  canShowPatternToday,
} from '../services/encouragement/MessageGenerator';
import {
  getAccountMessages,
  saveMessage,
  recordFeedback,
} from '../services/encouragement/FeedbackCollector';

interface UseEncouragementResult {
  currentMessage: EncouragementMessage | null;
  messageHistory: EncouragementMessage[];
  isLoading: boolean;

  checkForEncouragement: (
    accountId: string,
    completions: CompletionEntry[],
    previousStreak: number
  ) => Promise<void>;

  dismissMessage: () => void;

  submitFeedback: (messageId: string, rating: FeedbackRating) => Promise<void>;

  loadMessageHistory: (accountId: string) => Promise<void>;
}

export const useEncouragement = (): UseEncouragementResult => {
  const [currentMessage, setCurrentMessage] = useState<EncouragementMessage | null>(null);
  const [messageHistory, setMessageHistory] = useState<EncouragementMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Load message history for an account
   */
  const loadMessageHistory = useCallback(async (accountId: string) => {
    setIsLoading(true);
    try {
      const messages = await getAccountMessages(accountId);
      setMessageHistory(messages);
    } catch (error) {
      console.error('Failed to load message history:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Check if encouragement should be shown
   */
  const checkForEncouragement = useCallback(
    async (
      accountId: string,
      completions: CompletionEntry[],
      previousStreak: number
    ): Promise<void> => {
      try {
        // Detect pattern
        const pattern = detectPattern(completions, previousStreak);

        if (!pattern) {
          return;
        }

        // Check if pattern should be shown (based on feedback)
        if (!shouldShowPattern(pattern, messageHistory)) {
          console.log(`Pattern ${pattern} skipped due to negative feedback`);
          return;
        }

        // Check if pattern was already shown today
        if (!canShowPatternToday(pattern, messageHistory)) {
          console.log(`Pattern ${pattern} already shown today`);
          return;
        }

        // Create and save message
        const message = createEncouragementMessage(accountId, pattern);
        await saveMessage(message);

        // Show message to user
        setCurrentMessage(message);

        // Refresh history
        await loadMessageHistory(accountId);
      } catch (error) {
        console.error('Failed to check for encouragement:', error);
      }
    },
    [messageHistory, loadMessageHistory]
  );

  /**
   * Dismiss current message without feedback
   */
  const dismissMessage = useCallback(() => {
    setCurrentMessage(null);
  }, []);

  /**
   * Submit feedback for a message
   */
  const submitFeedback = useCallback(
    async (messageId: string, rating: FeedbackRating): Promise<void> => {
      try {
        await recordFeedback(messageId, rating);

        // Update local state
        setMessageHistory(prev =>
          prev.map(m =>
            m.id === messageId
              ? { ...m, feedbackRating: rating, feedbackAt: Date.now() }
              : m
          )
        );

        // Dismiss message after feedback
        setCurrentMessage(null);
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    },
    []
  );

  return {
    currentMessage,
    messageHistory,
    isLoading,
    checkForEncouragement,
    dismissMessage,
    submitFeedback,
    loadMessageHistory,
  };
};
